import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  hasCompleteEmergencyContacts,
  initialsOf,
  MINIMUM_EMERGENCY_CONTACTS,
} from '@domain/entities/Person';
import type { EmergencyContact, Person } from '@domain/entities/Person';
import { resolveEntitlement } from '@domain/policies/TenurePolicy';
import {
  PERSON_NAME_MAX_LENGTH,
  PersonName,
  personNameMessage,
} from '@domain/value-objects/PersonName';
import {
  formatPhoneDigits,
  formatStoredPhone,
  PHONE_NUMBER_LENGTH,
  PhoneNumber,
  phoneDigitsOf,
  phoneNumberMessage,
} from '@domain/value-objects/PhoneNumber';
import { PARENT_ROLE_LABEL, ROLE_LABEL, SHIRT_SIZE_LABEL } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import {
  Avatar,
  Banner,
  Card,
  Chip,
  EmptyState,
  Pagination,
  Spinner,
  cssVars,
  cx,
} from '../../design-system';
import { PersonalRecordCard } from '../PersonDetail/PersonalRecordCard';
import { ConfirmDataModal } from '../Onboarding/ConfirmDataModal';
import {
  formatDateRange,
  formatDay,
  formatDayWithYear,
  positionLabel,
  reasonFor,
  statusChipColors,
} from '../../format';
import { useAccess } from '../../hooks/useAccess';
import { useAreaName } from '../../hooks/useAreaName';
import type { LiveBalance } from '../../balancePeriod';
import { useLiveBalances } from '../../hooks/useLiveBalances';
import { usePagination } from '../../hooks/usePagination';
import { useRuntimeConfigState } from '../../providers/RuntimeConfigProvider';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import styles from './ProfileScreen.module.css';
import { WhatsAppLinkCard } from './WhatsAppLinkCard';
import { loadFailed } from '../../loadError';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Perfil del empleado. Los textos y las piezas salen de
 * `design/reference/screens/perfil-del-empleado.html`; **el acomodo ya no.**
 *
 * El prototipo lo reparte en dos columnas —identidad a la izquierda, números e
 * historial a la derecha—, y con datos de verdad eso se leía disperso: diez
 * renglones de identidad en una lista sin agrupar, una celda de saldo huérfana
 * en su propia fila, y un hueco de cientos de píxeles debajo de la columna que
 * acababa antes. El hueco se intentó tapar dos veces —ver la hoja de estilos— y
 * las dos cambiaron un problema por otro, porque el historial crece con cada
 * solicitud y la otra columna no.
 *
 * Desde el 7 oct 2026 son franjas a todo el ancho, una debajo de otra, y dos
 * mitades: **quién eres** —la cabecera y lo que guardas sobre ti— y **tus
 * vacaciones** —los números, el historial, a quién cubriste y la política—. Sin
 * columnas emparejadas no hay hueco que tapar, y lo que crece queda abajo.
 *
 * The emergency contacts are the sensitive part: third-party phone numbers, given
 * by the employee, about people who never used this system. They are self-managed
 * here and read elsewhere only behind the `ver_sos` permission.
 */

/*
 * Las tres cosas que una persona guarda sobre sí misma, en un solo panel.
 *
 * El expediente entró como tarjeta suelta entre los números y el historial, y
 * ahí no pertenece: eso es lo que el portal calculó sobre ella —saldo,
 * solicitudes, política—, y el expediente es lo que ella contestó, igual que los
 * contactos de emergencia y las preferencias de aviso. Las tres responden a «mis
 * datos», así que comparten pestañas.
 *
 * **El orden es Avisos, Expediente, Emergencia**, y se abre en Avisos: lo pidió
 * JC el 7 oct 2026. Es donde se vincula el WhatsApp, que es lo que hoy se le
 * pide hacer a quien entra a su perfil.
 */
type ProfileTab = 'avisos' | 'expediente' | 'sos';

/** Cuántas solicitudes se traen para el historial. Ver la consulta. */
const HISTORY_LIMIT = 50;

export const ProfileScreen = (): ReactNode => {
  const session = useSession();
  /* La configuración viva, y si ya llegó. `session.config` es la misma —el
     proveedor la reinyecta en cada snapshot—, pero no dice si lo que trae es de
     fábrica o del servidor, y aquí esa diferencia se ve en pantalla. */
  const { config, loaded: configLoaded } = useRuntimeConfigState();
  const areaName = useAreaName();
  const [tab, setTab] = useState<ProfileTab>('avisos');
  const [corrigiendo, setCorrigiendo] = useState(false);

  const person = useQuery({
    queryKey: ['profile', session.personId],
    queryFn: async () => {
      const result = await getServices().people.getById(session.personId);
      if (!result.ok) throw loadFailed('No pudimos cargar tu perfil.', result.error);
      return result.value;
    },
  });

  /*
   * Sus líderes. Se resuelven aquí y no se toman de la sesión porque el vínculo
   * vive en `personLeaders`, persona por persona, y cambia sin que la sesión se
   * rehaga.
   */
  const myLeaders = useQuery({
    queryKey: ['profile', 'leaders', session.personId],
    queryFn: async () => {
      const links = await getServices().leaders.leadersOf(session.personId, session.areaId);
      /* Un fallo no es «nadie te firma». Ver la misma cuenta en la ficha de una
         persona y el porqué en `leadersOf`. */
      if (!links.ok) return { failed: true, people: [] as readonly Person[] };
      if (links.value.length === 0) return { failed: false, people: [] as readonly Person[] };
      const people = await getServices().people.getMany(links.value.map((l) => l.leaderPersonId));
      return { failed: false, people: people.ok ? people.value : ([] as readonly Person[]) };
    },
  });

  const balance = useLiveBalances({
    scope: 'profile',
    personId: session.personId,
    hireDate: session.hireDate,
    expiryMonths: session.config.rules.expiryMonths,
  });

  const history = useQuery({
    queryKey: ['profile', 'history', session.personId],
    queryFn: async () => {
      /* Cincuenta y no doce: el historial se pagina de diez en diez, y con doce
         la segunda página eran dos renglones y el resto de la historia no
         existía. Firestore cobra por documento devuelto, así que sólo lee más
         quien de verdad tiene más. */
      const result = await getServices().requests.list(
        { personId: session.personId },
        { limit: HISTORY_LIMIT },
      );
      return result.ok ? result.value.items : [];
    },
  });

  /** Absences this person was named to cover. A separate, indexed query. */
  const covered = useQuery({
    queryKey: ['profile', 'covered', session.personId],
    queryFn: async () => {
      const result = await getServices().requests.list(
        { replacementPersonId: session.personId },
        { limit: 6 },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const subArea = useQuery({
    queryKey: ['profile', 'subArea', session.subAreaId],
    enabled: session.subAreaId !== null,
    queryFn: async () => {
      if (session.subAreaId === null) return null;
      const result = await getServices().organization.getSubArea(session.subAreaId);
      return result.ok ? result.value : null;
    },
  });

  const contacts = useQuery({
    queryKey: ['profile', 'contacts', session.personId],
    queryFn: async () => {
      const result = await getServices().contacts.listFor(session.personId);
      return result.ok ? result.value : ([] as readonly EmergencyContact[]);
    },
  });

  /* El historial, de diez en diez. Antes de los retornos de abajo: un hook no
     puede ir después de ellos. */
  const historyPager = usePagination(history.data ?? []);

  if (person.isLoading) {
    return (
      <Card>
        <Spinner label="Cargando tu perfil" />
      </Card>
    );
  }

  if (person.error !== null || person.data === undefined) {
    return <Banner tone="danger">No pudimos cargar tu perfil.</Banner>;
  }

  const me = person.data;
  const rules = session.config.rules;

  const entitlement = resolveEntitlement({
    hireDate: me.hireDate,
    asOf: getServices().clock.today(),
    seniority: rules.seniority,
    legalMinimumDays: rules.legalMinimumDays,
  });

  const requests = history.data ?? [];
  const totalDays = requests
    .filter((r) => r.status === 'Aprobada' || r.status === 'Tomada')
    .reduce((sum, r) => sum + r.businessDays, 0);

  /*
   * El puesto, que el portal guardaba y no enseñaba en ninguna pantalla.
   *
   * 298 de las 339 personas activas lo tienen desde la carga del padrón —medido
   * contra producción el 24 ago 2026—, y hasta hoy sólo se veía en la tarjeta de
   * confirmación del primer inicio de sesión: una vez, y nunca más.
   *
   * **Y no se pinta hasta que la configuración llegue.** Los tres casos —sin
   * puesto, sin catálogo todavía, y con las dos cosas— los reparte
   * `positionLabel`, que es donde está contado por qué: la ficha de la persona
   * llegó a enseñar «ios-tech-lead-developer» en crudo. Aquí sólo se decide qué
   * poner cuando no hay puesto, porque este renglón necesita un valor y la ficha
   * de RR.HH. prefiere no dibujar la línea.
   */
  const puesto =
    positionLabel({
      positionId: me.positionId,
      positions: config.catalogs.positions,
      configLoaded,
    }) ?? 'Sin registrar';

  /*
   * Los diez datos de la cabecera, en tres grupos con su rótulo.
   *
   * Eran una sola lista, con los tres del libro de eventos al final «y no
   * mezclados con los de trabajo» — pero sin nada que dijera dónde acababa una
   * cosa y empezaba la otra, y diez renglones seguidos no se leen: se recorren.
   * Dónde estás, desde cuándo, y lo tuyo.
   */
  const placementFields: readonly IdentityField[] = [
    { key: 'Puesto', value: puesto },
    { key: 'Área', value: areaName },
    { key: 'Sub área', value: subArea.data?.name ?? 'Sin asignar' },
    /*
     * Quién le firma, en su propio perfil.
     *
     * Hasta ahora sólo se enteraba al final del asistente de solicitud, cuando ya
     * la había enviado — o sea que quien quería saber a quién le llegaría su
     * petición tenía que empezar una para averiguarlo. Es el dato que decide si
     * sus vacaciones se aprueban, y estaba escondido detrás de un formulario.
     *
     * Con dos o más líderes se piden dos firmas, así que enseñarlos a todos no es
     * adorno: explica por qué su solicitud espera más.
     */
    {
      key:
        myLeaders.data === undefined || myLeaders.data.failed
          ? 'Te firman'
          : myLeaders.data.people.length === 1
            ? 'Te firma'
            : 'Te firman',
      value:
        myLeaders.data === undefined
          ? '…'
          : myLeaders.data.failed
            ? /* «Nadie todavía» le diría a alguien que sus vacaciones no van a
                 llegar a ningún sitio, cuando lo único que pasó es que no se pudo
                 consultar. */
              'No pudimos consultarlo — vuelve a cargar la página'
            : myLeaders.data.people.length === 0
              ? 'Nadie todavía — pídeselo a RR.HH.'
              : myLeaders.data.people.map((l) => l.displayName).join(' · '),
    },
  ];

  const companyFields: readonly IdentityField[] = [
    { key: 'Correo', value: me.email },
    {
      key: 'Ingreso',
      value: me.hireDate === null ? 'Sin registrar' : formatDay(me.hireDate),
    },
    {
      key: 'Antigüedad',
      value:
        entitlement.completedYears === 0
          ? 'Menos de un año'
          : `${String(entitlement.completedYears)} ${
              entitlement.completedYears === 1 ? 'año' : 'años'
            }`,
    },
  ];

  const personalFields: readonly IdentityField[] = [
    /*
     * Los tres del libro de eventos.
     *
     * **Con año**, a diferencia de «Ingreso», que se pinta con `formatDay`: en la
     * fecha de ingreso el año se deduce de la antigüedad que va justo debajo, y en
     * un nacimiento el año *es* el dato. Sin él, «12 de agosto» no distingue a
     * quien cumple cuarenta de quien cumple veinte.
     *
     * «Sin registrar» y no una línea escondida: son 20 fichas sin nacimiento y 64
     * sin talla, y verlo vacío es lo que hace que alguien lo reporte desde la
     * tarjeta de confirmación. Una fila que desaparece no le pide nada a nadie.
     */
    {
      key: 'Nacimiento',
      value: me.birthDate === null ? 'Sin registrar' : formatDayWithYear(me.birthDate),
    },
    {
      key: 'Talla',
      value: me.shirtSize === null ? 'Sin registrar' : SHIRT_SIZE_LABEL[me.shirtSize],
    },
    /*
     * Pregunta, y no rótulo. «Papá o mamá — Sin registrar» se leyó en producción
     * como «dime quién es tu mamá»: alguien contestó con el nombre de la suya.
     * Un renglón que hay que interpretar produce respuestas a otra pregunta.
     *
     * Y «Sin responder» y no «Sin registrar», que es lo que dicen los demás: los
     * otros son datos que RR.HH. no ha capturado, y éste es una pregunta que su
     * dueño todavía no ha contestado. Se contesta desde «Solicitar corrección de
     * datos».
     */
    {
      key: '¿Eres papá o mamá?',
      /* Sin «Sí,» delante: el rótulo ya es la pregunta, y anteponerlo daba «Sí,
         Papá» —mayúscula a media frase— porque el rótulo del catálogo está
         escrito para ir suelto. */
      value: me.parentRole === null ? 'Sin responder' : PARENT_ROLE_LABEL[me.parentRole],
    },
  ];

  const stats: readonly { label: string; value: number | string; note: string; color: string }[] = [
    {
      label: 'Días por antigüedad',
      value: entitlement.days,
      note: 'Lo que te corresponde este año',
      color: 'var(--color-brand)',
    },
    /* Una celda por periodo vigente: lo que queda del anterior sigue valiendo
       junto al nuevo, se gasta primero, y cada uno vence en su fecha. */
    ...(balance.data === undefined || balance.data.length === 0
      ? [
          {
            label: 'Disponibles',
            value: '—',
            note: 'Listos para solicitar',
            color: 'var(--color-success)',
          },
        ]
      : balance.data.map((live) => ({
          label: `Disponibles ${String(live.year)}`,
          value: live.available,
          note: `Hasta el ${formatDayWithYear(live.lastValidDay)}`,
          color: 'var(--color-success)',
        }))),
    {
      label: 'Agendados',
      value: sumOver(balance.data, 'scheduledDays'),
      note: 'Aprobados, aún por tomar',
      color: 'var(--color-violet)',
    },
    {
      label: 'En revisión',
      value: sumOver(balance.data, 'pendingDays'),
      note: 'Esperando decisión',
      color: 'var(--color-warning)',
    },
  ];

  const policy: readonly { label: string; value: string }[] = [
    { label: 'Mínimo legal al año', value: `${String(rules.legalMinimumDays)} días` },
    { label: 'Días mínimos por solicitud', value: String(rules.minimumRequestDays) },
    { label: 'Anticipación esperada', value: `${String(rules.noticeDays)} días` },
    {
      label: 'Tus días valen desde tu aniversario',
      value: `${String(rules.expiryMonths)} meses`,
    },
    {
      label: 'Máximo de personas fuera a la vez en tu sub área',
      value: String(subArea.data?.maxConcurrentOut ?? rules.defaultMaxConcurrentOut),
    },
  ];

  return (
    <div className="flow">
      {/*
        La misma tarjeta que sale la primera vez, abierta a voluntad.

        Se reutiliza en vez de escribir un formulario de reporte aparte porque
        entonces habría dos sitios que enseñan «lo que el portal cree de ti», y
        este repositorio ya sabe cómo acaba eso: uno de los dos se queda atrás.
        `onCancel` es lo único que la distingue — con él se puede cerrar.
      */}
      {corrigiendo && (
        <ConfirmDataModal
          person={me}
          onDone={() => {
            setCorrigiendo(false);
          }}
          onCancel={() => {
            setCorrigiendo(false);
          }}
        />
      )}

      <PageTitle className={styles.title}>Perfil del colaborador</PageTitle>

      <div className={styles.page}>
        {/* ---- Quién eres: la cabecera ---- */}
        <section className={styles.identityCard} aria-label="Tus datos en la empresa">
          <div className={styles.identityTop}>
            {me.photoURL !== null && me.photoURL.length > 0 ? (
              <img src={me.photoURL} alt="" className={styles.identityAvatar} />
            ) : (
              <div className={styles.identityAvatar} aria-hidden="true">
                {initialsOf(me.displayName)}
              </div>
            )}
            <div className={styles.identityText}>
              <h2 className={styles.identityName}>{me.displayName}</h2>
              <p className={styles.identityRole}>{ROLE_LABEL[me.role]}</p>
            </div>

            {/*
              Nadie edita aquí su área, su fecha de ingreso ni su rol: mueven el
              derecho y a quién le llega la firma, así que un cambio es una
              petición a RR.HH. y no un formulario.

              **Hasta hoy este botón sólo enseñaba un toast.** Decía «le avisamos
              a RR.HH.» y no avisaba a nadie — no había ni llamada ni documento
              detrás. Ahora abre la misma tarjeta de «Confirma tus datos», que es
              la que sí escribe el reporte y le llega a quien administra el área.
            */}
            <button
              type="button"
              className={styles.identityAction}
              onClick={() => {
                setCorrigiendo(true);
              }}
            >
              Solicitar corrección de datos
            </button>
          </div>

          <div className={styles.identityGroups}>
            <IdentityGroup label="Puesto y equipo" fields={placementFields} />
            <IdentityGroup label="En la empresa" fields={companyFields} />
            <IdentityGroup label="Datos personales" fields={personalFields} />
          </div>
        </section>

        {/* ---- Lo que guardas sobre ti ---- */}
        <section className={styles.panel}>
          <div className={styles.panelHead}>
            <div className={styles.tabTrack} role="tablist" aria-label="Secciones del perfil">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'avisos'}
                className={cx(styles.tab, tab === 'avisos' && styles.tabActive)}
                onClick={() => {
                  setTab('avisos');
                }}
              >
                Avisos
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'expediente'}
                className={cx(styles.tab, tab === 'expediente' && styles.tabActive)}
                onClick={() => {
                  setTab('expediente');
                }}
              >
                Expediente
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'sos'}
                className={cx(styles.tab, tab === 'sos' && styles.tabActive)}
                onClick={() => {
                  setTab('sos');
                }}
              >
                Emergencia
                <span className={styles.tabBadge}>{(contacts.data ?? []).length}</span>
              </button>
            </div>

            {tab === 'sos' && (
              <Chip
                background={
                  hasCompleteEmergencyContacts(contacts.data ?? [])
                    ? 'var(--color-success-bg)'
                    : 'var(--color-warning-bg)'
                }
                foreground={
                  hasCompleteEmergencyContacts(contacts.data ?? [])
                    ? 'var(--color-success-ink)'
                    : 'var(--color-warning-ink)'
                }
              >
                {hasCompleteEmergencyContacts(contacts.data ?? []) ? 'Completo' : 'Incompleto'}
              </Chip>
            )}
          </div>

          <p className={styles.panelNote}>
            {tab === 'sos'
              ? 'Sólo tú, tus líderes y RR.HH. pueden ver estos números. Se consultan cuando estás fuera y algo urgente ocurre.'
              : tab === 'expediente'
                ? 'Lo que contestaste en el formulario de la familia Knotion. Aquí sólo se lee: si algo está mal, usa «Solicitar corrección de datos».'
                : 'Elige por dónde quieres enterarte del estado de tus solicitudes.'}
          </p>

          {tab === 'sos' ? (
            <EmergencyTab />
          ) : tab === 'expediente' ? (
            /* Sin marco propio: el panel ya pone el borde y la pestaña ya dice
               de qué es. Ver `frame` en `PersonalRecordCard`. */
            <PersonalRecordCard person={me} ownProfile frame="plain" />
          ) : (
            <NotificationsTab person={me} />
          )}
        </section>

        {/* ---- Tus vacaciones: números, historial, a quién cubriste, política ---- */}
        <h2 className={styles.bandTitle}>Tus vacaciones</h2>

        <div className={styles.stats}>
          {stats.map((stat) => (
            <div
              key={stat.label}
              className={styles.stat}
              style={cssVars({ '--stat-color': stat.color })}
            >
              <div className={styles.statLabel}>{stat.label}</div>
              <div className={styles.statValue}>{stat.value}</div>
              <div className={styles.statNote}>{stat.note}</div>
            </div>
          ))}
        </div>

        <section className={styles.panel}>
          <div className={styles.sectionHead}>
            <h3 className={styles.sectionTitle}>Historial de vacaciones</h3>
            <span className={styles.sectionMeta}>{totalDays} días registrados</span>
          </div>

          {requests.length === 0 ? (
            <EmptyState
              glyph="↗"
              title="Sin historial todavía"
              note="Cuando envíes tu primera solicitud aparecerá aquí."
            />
          ) : (
            <div className={cx(styles.historyScroll, 'scroll-x')}>
              <div className={styles.historyGrid}>
                <div className={styles.historyHead}>
                  <div className={styles.historyHeadCell}>Periodo</div>
                  <div className={styles.historyHeadCell}>Días</div>
                  <div className={styles.historyHeadCell}>Reemplazo</div>
                  <div className={styles.historyHeadCell}>Estado</div>
                </div>

                {historyPager.items.map((request) => {
                  const colors = statusChipColors(request.status);
                  return (
                    <div key={request.id} className={styles.historyRow}>
                      <span className={styles.historyRange}>{formatDateRange(request.range)}</span>
                      <span className={styles.historyDays}>{request.businessDays}</span>
                      <span className={styles.historyRepl}>
                        {request.replacementName.length > 0 ? request.replacementName : '—'}
                      </span>
                      <span>
                        <Chip background={colors.background} foreground={colors.foreground}>
                          {request.status}
                        </Chip>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Pagination pager={historyPager} noun="solicitudes" />
        </section>

        <div className={styles.pair}>
          <section className={styles.panel}>
            <h3 className={styles.pairTitle}>Reemplazos que cubriste</h3>
            {(covered.data ?? []).length === 0 ? (
              <EmptyState
                glyph="↺"
                title="Todavía ninguno"
                note="Aparecerán aquí cuando alguien te nombre su reemplazo."
              />
            ) : (
              <ul className={styles.coverList}>
                {(covered.data ?? []).map((request) => {
                  const colors = statusChipColors(request.status);
                  return (
                    <li key={request.id} className={styles.coverRow}>
                      <Avatar name={request.personName} color="var(--color-brand)" size={36} />
                      <div className={styles.coverText}>
                        <div className={styles.coverName}>{request.personName}</div>
                        <div className={styles.coverMeta}>
                          {formatDateRange(request.range)} · {request.folio}
                        </div>
                      </div>
                      <Chip background={colors.background} foreground={colors.foreground}>
                        {request.status}
                      </Chip>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className={styles.panel}>
            <h3 className={styles.pairTitle}>Política que te aplica</h3>
            <div className={styles.policyList}>
              {policy.map((row) => (
                <div key={row.label} className={styles.policyRow}>
                  <span className={styles.policyLabel}>{row.label}</span>
                  <strong className={styles.policyValue}>{row.value}</strong>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

interface IdentityField {
  readonly key: string;
  readonly value: string;
}

/**
 * Un grupo de datos de la cabecera: su rótulo y sus renglones.
 *
 * Los renglones son los del prototipo —la etiqueta a la izquierda, el valor a la
 * derecha, una raya debajo—; lo que cambia es que ya no van los diez en fila
 * sino tres grupos, uno al lado del otro mientras quepan.
 */
const IdentityGroup = ({
  label,
  fields,
}: {
  label: string;
  fields: readonly IdentityField[];
}): ReactNode => (
  <div className={styles.identityGroup}>
    <h3 className={styles.identityGroupLabel}>{label}</h3>
    <dl className={styles.identityFields}>
      {fields.map((field) => (
        <div key={field.key} className={styles.identityField}>
          <dt className={styles.identityKey}>{field.key}</dt>
          <dd className={styles.identityValue}>{field.value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

/**
 * Emergency contacts.
 *
 * The handoff asks for at least two, in order of preference, with a warning when
 * one is missing — because the whole point is that somebody is reachable when the
 * first number does not answer.
 */
const EmergencyTab = (): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  /*
   * `phone` guarda **dígitos**, no lo que se ve.
   *
   * La máscara es de la vista: el campo pinta `formatPhoneDigits(draft.phone)` y
   * devuelve dígitos al escribir. Guardar aquí el texto con espacios obligaría a
   * limpiarlo en cada lectura y dejaría la puerta abierta a que se guardara una
   * cadena con formato, que es justo lo que hay en producción hoy.
   */
  const [draft, setDraft] = useState({ name: '', relation: '', phone: '' });

  const contacts = useQuery({
    queryKey: ['profile', 'contacts', session.personId],
    queryFn: async () => {
      const result = await getServices().contacts.listFor(session.personId);
      return result.ok ? result.value : ([] as readonly EmergencyContact[]);
    },
  });

  const list = contacts.data ?? [];
  const complete = hasCompleteEmergencyContacts(list);

  const name = PersonName.create(draft.name);
  const phone = PhoneNumber.create(draft.phone);
  const valid = name.ok && phone.ok;

  /*
   * El error aparece cuando hay algo escrito, no antes.
   *
   * Un formulario que reclama sobre un campo vacío que nadie ha tocado se lee
   * como que ya se hizo algo mal; el botón deshabilitado ya dice que falta.
   */
  const nameError = !name.ok && draft.name.trim().length > 0 ? personNameMessage(name.error) : null;
  const phoneError = !phone.ok && draft.phone.length > 0 ? phoneNumberMessage(phone.error) : null;

  const save = async (): Promise<void> => {
    if (!name.ok || !phone.ok) return;

    const result = await getServices().contacts.create({
      personId: session.personId,
      areaId: session.areaId,
      // Lo normalizado, no lo tecleado: sin espacios de sobra y sin la lada de país.
      name: name.value.value,
      relation: draft.relation.trim(),
      phone: phone.value.value,
    });

    if (!result.ok) {
      toast.bad(`No se pudo guardar el contacto.${reasonFor(result.error)}`);
      return;
    }

    toast.ok('Contacto agregado.');
    setDraft({ name: '', relation: '', phone: '' });
    setAdding(false);
    await contacts.refetch();
  };

  /*
   * Both of these announced success without looking at the result.
   *
   * Harmless while the repository behind them was the in-memory fake, which
   * cannot fail. Against Firestore it is the failure mode this screen exists to
   * avoid: «eliminado» over a contact still on the list, or a calling order the
   * rules refused to change while the toast said otherwise.
   */
  const makeFirst = async (contact: EmergencyContact): Promise<void> => {
    const reordered = [contact.id, ...list.filter((c) => c.id !== contact.id).map((c) => c.id)];
    const result = await getServices().contacts.reorder(session.personId, reordered);

    if (!result.ok) {
      toast.bad(`No se pudo cambiar el orden.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(`${contact.name} quedó como primer contacto.`);
    await contacts.refetch();
  };

  const remove = async (contact: EmergencyContact): Promise<void> => {
    const result = await getServices().contacts.delete(contact.id);

    if (!result.ok) {
      toast.bad(`No se pudo eliminar el contacto.${reasonFor(result.error)}`);
      return;
    }
    toast.info(`${contact.name} eliminado.`);
    await contacts.refetch();
  };

  return (
    <>
      {!complete && (
        <div className={styles.sosWarn}>
          <span className={styles.sosWarnGlyph} aria-hidden="true">
            !
          </span>
          <span className={styles.sosWarnText}>
            Registra al menos {MINIMUM_EMERGENCY_CONTACTS} contactos. Si el primero no contesta,
            alguien más tiene que poder responder.
          </span>
        </div>
      )}

      {list.length === 0 ? (
        <EmptyState glyph="☎" title="Sin contactos de emergencia" />
      ) : (
        <ul className={styles.sosList}>
          {list.map((contact, index) => (
            <li key={contact.id} className={styles.sosCard}>
              <div className={styles.sosTop}>
                <Avatar name={contact.name} color="var(--color-brand)" size={38} />
                <span className={styles.sosBody}>
                  <span className={styles.sosName}>{contact.name}</span>
                  <span className={styles.sosMeta}>
                    {contact.relation.length > 0 ? `${contact.relation} · ` : ''}
                    {/* Lo guardado son diez dígitos; el formato es de aquí. Un
                        registro anterior a la validación se enseña tal cual. */}
                    {formatStoredPhone(contact.phone)}
                  </span>
                </span>
                <Chip
                  background={
                    index === 0 ? 'var(--color-success-bg)' : 'var(--color-surface-subtle-alt)'
                  }
                  foreground={
                    index === 0 ? 'var(--color-success-ink)' : 'var(--color-text-tertiary-alt)'
                  }
                >
                  {index === 0 ? '1º · primero' : `${String(index + 1)}º`}
                </Chip>
              </div>

              <div className={styles.sosActions}>
                {index > 0 && (
                  <button
                    type="button"
                    className={styles.sosPromote}
                    onClick={() => {
                      void makeFirst(contact);
                    }}
                  >
                    Hacer primero
                  </button>
                )}
                <button
                  type="button"
                  className={styles.sosRemove}
                  onClick={() => {
                    void remove(contact);
                  }}
                >
                  Quitar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className={styles.sosForm}>
          <span className={styles.sosFormTitle}>Nuevo contacto</span>

          <LabelledInput
            label="Nombre completo"
            placeholder="Ej. Lucía Rivas"
            value={draft.name}
            error={nameError}
            maxLength={PERSON_NAME_MAX_LENGTH}
            onChange={(value) => {
              setDraft((d) => ({ ...d, name: value }));
            }}
          />
          <LabelledInput
            label="Parentesco"
            placeholder="Ej. Hermana"
            value={draft.relation}
            onChange={(value) => {
              setDraft((d) => ({ ...d, relation: value }));
            }}
          />
          <LabelledInput
            /* La etiqueta es la del prototipo y sigue siendo exacta: en México la
               lada son los dos o tres primeros de los diez dígitos. */
            label="Teléfono con lada"
            /* El marcador de posición **no** es el del prototipo, que dice
               `+52 55 0000 0000`. El campo ya no puede contener esa forma —el 52
               de país se descarta al escribirlo— y un ejemplo que el propio
               control se niega a sostener enseña a teclear algo que va a
               desaparecer. Éste es lo que el campo produce. */
            placeholder="55 1234 5678"
            type="tel"
            /*
             * El campo enseña la máscara y el estado guarda dígitos. Lo que se
             * teclee se limpia al vuelo —letras, signos, la lada de país de un
             * número pegado— y se corta en diez, así que no hay forma de escribir
             * algo que la validación tenga que rechazar por su forma.
             */
            value={formatPhoneDigits(draft.phone)}
            error={phoneError}
            inputMode="numeric"
            onChange={(value) => {
              setDraft((d) => ({ ...d, phone: phoneDigitsOf(value).slice(0, PHONE_NUMBER_LENGTH) }));
            }}
          />

          <div className={styles.sosFormActions}>
            <button
              type="button"
              className={styles.sosSave}
              disabled={!valid}
              onClick={() => {
                void save();
              }}
            >
              Guardar contacto
            </button>
            <button
              type="button"
              className={styles.sosCancel}
              onClick={() => {
                setAdding(false);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={styles.sosAdd}
          onClick={() => {
            setAdding(true);
          }}
        >
          + Agregar contacto
        </button>
      )}
    </>
  );
};

/**
 * A label wrapping its own control.
 *
 * The design nests the input inside the `<label>`, which associates the two
 * without needing a generated id — and keeps the whole thing clickable.
 */
const LabelledInput = ({
  label,
  value,
  placeholder,
  type = 'text',
  error = null,
  inputMode,
  maxLength,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  type?: 'text' | 'tel';
  /** El motivo, ya redactado. `null` mientras no haya nada que decir. */
  error?: string | null;
  inputMode?: 'numeric';
  maxLength?: number;
  onChange: (value: string) => void;
}): ReactNode => (
  <label className={styles.sosField}>
    {label}
    <input
      type={type}
      className={cx(styles.sosInput, error !== null && styles.sosInputBad)}
      placeholder={placeholder}
      value={value}
      inputMode={inputMode}
      maxLength={maxLength}
      /* El mensaje va colgado del campo, no suelto debajo: un lector de pantalla
         lo lee al entrar en el control, que es cuando sirve. */
      aria-invalid={error !== null}
      aria-errormessage={error === null ? undefined : `${label}-error`}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
    {error !== null && (
      <span id={`${label}-error`} className={styles.sosFieldError}>
        {error}
      </span>
    )}
  </label>
);

/** The only part of a person a client may write directly. */
const NotificationsTab = ({ person }: { person: Person }): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const { featureOn } = useAccess();

  const current = useQuery({
    queryKey: ['profile', session.personId],
    queryFn: async () => {
      const result = await getServices().people.getById(session.personId);
      return result.ok ? result.value : null;
    },
  });

  const prefs = current.data?.notificationPrefs ?? person.notificationPrefs;

  const toggle = async (key: keyof typeof prefs): Promise<void> => {
    const next = { ...prefs, [key]: !prefs[key] };
    const result = await getServices().people.updateNotificationPreferences(session.personId, next);
    if (!result.ok) {
      toast.bad('No se pudo guardar la preferencia.');
      return;
    }
    await current.refetch();
  };

  const OPTIONS: readonly { key: keyof typeof prefs; label: string; note: string }[] = [
    { key: 'email', label: 'Correo', note: 'Estado de tus solicitudes y documentos pendientes' },
    { key: 'slack', label: 'Slack', note: 'El mismo aviso, en tu canal directo' },
    {
      key: 'reminder',
      label: 'Recordatorio previo',
      note: 'Tres días antes de tu primer día fuera',
    },
  ];

  /* El asistente, sólo con su módulo encendido: apagado, ni la callable
     contesta, y una tarjeta que ofrece vincular algo que no existe promete lo
     que no puede cumplir. Y decide el reparto: con él son dos mitades. */
  const assistant = featureOn('whatsapp');

  return (
    <div className={cx(styles.prefs, assistant && styles.prefsSplit)}>
      <div className={styles.prefList}>
        {OPTIONS.map((option) => (
          <button
            key={option.key}
            type="button"
            className={styles.pref}
            role="switch"
            aria-checked={prefs[option.key]}
            onClick={() => {
              void toggle(option.key);
            }}
          >
            <span className={styles.prefText}>
              <span className={styles.prefLabel}>{option.label}</span>
              <span className={styles.prefNote}>{option.note}</span>
            </span>
            <span className={cx(styles.prefTrack, prefs[option.key] && styles.prefTrackOn)}>
              <span className={cx(styles.prefKnob, prefs[option.key] && styles.prefKnobOn)} />
            </span>
          </button>
        ))}
      </div>
      {assistant && <WhatsAppLinkCard />}
    </div>
  );
};

/** Un contador sumado entre los periodos vigentes, o «—» mientras no hay saldo. */
const sumOver = (
  lives: readonly LiveBalance[] | undefined,
  field: 'scheduledDays' | 'pendingDays',
): number | string =>
  lives === undefined || lives.length === 0
    ? '—'
    : lives.reduce((sum, live) => sum + live.balance[field], 0);
